import type { Cmyk } from '@flobi/artwork-core';
import { useId } from 'react';
import { Icon, ICON_SIZE, type IconName } from '../chrome/icons';
import { partsOf, type Rail, type RailButton, type RailField, type Well } from '../tools/types';
import { NumberField, Section, SegmentedControl, Slider } from '../../ui/controls';
import { tip } from '../../ui/tooltip';
import { ColourField } from './Paint';
import { MenuWell } from './MenuWell';

/**
 * The tool in hand, in the side panel: its ways of working, its quantities, what can be
 * done with what it made, and what it will draw with.
 *
 * Nothing here knows about any tool. Each tool still declares its rail — a top well, a
 * middle, a bottom, and whether the draw-with block belongs — and this draws that
 * declaration in the platform's controls: a well of marks where one is pressed becomes a
 * segmented control, a well of marks where none is becomes a row of actions, a slider
 * becomes a labelled slider with its number beside it. A new tool is still a file.
 *
 * A tool may declare panel families to show labeled tiles instead of an icon strip.
 * Their titles use "Name — instruction": the name stays visible and the selected
 * instruction sits below the families, while tooltips stay short.
 */

export interface DrawWith {
  readonly fill: string;
  /** The ink the fill stands for, in a CMYK file. */
  readonly fillInk?: Cmyk;
  readonly fillOn: boolean;
  readonly stroke: string;
  readonly strokeInk?: Cmyk;
  readonly weight: number;
  onFill(hex: string, ink?: Cmyk): void;
  onFillNone(): void;
  onStroke(hex: string, ink?: Cmyk): void;
  onStrokeNone(): void;
  onWeight(weight: number): void;
}

export function ToolOptions({ rail, drawWith, sectioned = true }: { rail: Rail | null; drawWith?: DrawWith; sectioned?: boolean }) {
  // Groups opened out, and the strip's line breaks dropped: this is one column already.
  const wells = partsOf([rail?.top, rail?.middle, rail?.bottom]).filter((well) => well.kind !== 'break');
  if (wells.length === 0 && !rail?.style) return null;
  const controls = <div className="grid grid-cols-[minmax(0,1fr)] gap-[12px]">{wells.map((well, index) => (
        <WellView key={index} well={well} />
      ))}</div>;
  return <>
    {wells.length > 0 ? sectioned ? <Section title="Tool settings" id="context-tool">{controls}</Section> : controls : null}
    {rail?.style && drawWith ? <Section title="Drawing style" id="context-drawing"><DrawWithBlock paint={drawWith} /></Section> : null}
  </>;
}

function WellView({ well }: { well: Well }) {
  if (well.kind === 'slider') return <SliderWell well={well} />;
  if (well.kind === 'menu') return <MenuWell well={well} placement="below" wide />;
  // The panel's own header already names what is being worked on (`rail.title`), and a
  // second copy of the name under it would be the same word twice.
  if (well.kind === 'text') return null;
  if (well.kind === 'fields') return <FieldsWell items={well.items} />;
  if (well.kind === 'group' || well.kind === 'break') return null;
  const items = well.items;
  // Buttons with names are rows with names: the marks the strip has no room to explain.
  if (items.some((item) => item.label)) return <ActionWell items={items} />;
  const modes = items.some((item) => item.pressed !== undefined);
  if (modes && items.some(item => item.panelGroup)) return <GroupedModes items={items} />;
  return modes ? <ModeWell items={items} /> : <ActionWell items={items} />;
}

/** Labeled families within one mode choice, with instructions for the active tool. */
function GroupedModes({ items }: { items: readonly RailButton[] }) {
  const helpId = useId();
  const selected = items.find(item => item.pressed);
  const groups = [...new Set(items.map(item => item.panelGroup ?? 'Tools'))];
  return <div className="tool-mode-families" role="group" aria-label="Drawing mode">
    {groups.map(group => <div className="tool-mode-family" role="group" aria-label={group} key={group}>
      <h3>{group}</h3>
      <div className="tool-mode-grid" data-count={items.filter(item => (item.panelGroup ?? 'Tools') === group).length}>
        {items.filter(item => (item.panelGroup ?? 'Tools') === group).map(item => {
          const { name } = split(item.title);
          return <button key={item.id} type="button" aria-pressed={!!item.pressed} aria-describedby={item.pressed ? helpId : undefined} disabled={item.disabled} {...tip(name)} onClick={() => item.run({ more: false })}>
            <Icon name={item.icon} size={ICON_SIZE.navigation} /><span>{name}</span>
          </button>;
        })}
      </div>
    </div>)}
    <p className="tool-mode-help" id={helpId}>{selected ? split(selected.title).rest : 'Choose a tool to begin.'}</p>
  </div>;
}

/** Choosing how the tool works: a segmented control of marks, and the chosen one's name. */
function ModeWell({ items }: { items: readonly RailButton[] }) {
  const chosen = items.find((item) => item.pressed) ?? null;
  const said = chosen ? split(chosen.title) : null;
  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-[6px]">
      <SegmentedControl
        label="Mode"
        value={chosen?.id ?? null}
        onChange={(id) => items.find((item) => item.id === id)?.run({ more: false })}
        segments={items.map((item) => ({ value: item.id, icon: item.icon as IconName, tip: item.title, disabled: item.disabled }))}
      />
      {said ? <p className="text-[12px] leading-[15px] text-label-2">{said.name}</p> : null}
    </div>
  );
}

/** What can be done with what the tool made: a row of labelled actions. */
function ActionWell({ items }: { items: readonly RailButton[] }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-[2px]">
      {items.map((item) => {
        const said = split(item.title);
        return (
          <button
            key={item.id}
            type="button"
            disabled={item.disabled}
            aria-pressed={item.pressed}
            {...(said.rest ? tip(said.rest) : {})}
            onClick={(event) => item.run({ more: event.metaKey || event.ctrlKey })}
            className="flex h-[28px] items-center gap-[8px] rounded-[6px] px-[10px] text-left text-[12px] text-label transition-colors hover:bg-fill-2 active:bg-fill-pressed disabled:text-label-3 disabled:hover:bg-transparent"
          >
            <span className="text-label-2">
              <Icon name={item.icon as IconName} size={16} />
            </span>
            <span className="min-w-0 flex-1 truncate">{item.label ?? said.name}</span>
            {item.pressed ? <Icon name="check" size={14} /> : null}
          </button>
        );
      })}
    </div>
  );
}

/** Quantities in play together: each a name, a number and a slider, one under another. */
function FieldsWell({ items }: { items: readonly RailField[] }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-[10px]">
      {items.map((field) => {
        const step = field.step ?? 1;
        const decimals = step < 1 ? Math.min(3, Math.ceil(-Math.log10(step))) : 0;
        const readable = field.parse ? { format: field.format, parse: field.parse } : {};
        return (
          <div key={field.id} className="grid grid-cols-[minmax(0,1fr)] gap-[2px]" {...tip(field.title)}>
            <div className="flex items-center gap-[8px]">
              <span className="min-w-0 flex-1 truncate text-[12px] text-label-2">{field.label}</span>
              <NumberField className="w-[96px]" label={field.label} value={field.value} min={field.min} max={field.max} step={step} decimals={decimals} {...readable} onChange={field.onChange} />
            </div>
            <Slider label={field.label} value={field.value} min={field.min} max={field.max} step={step} onChange={field.onChange} />
          </div>
        );
      })}
    </div>
  );
}

/** A quantity: its name, its number, and a slider under both. */
function SliderWell({ well }: { well: Extract<Well, { kind: 'slider' }> }) {
  const said = split(well.title);
  const step = well.step ?? 1;
  const decimals = step < 1 ? Math.min(3, Math.ceil(-Math.log10(step))) : 0;
  const suffix = well.format ? suffixOf(well.format(well.value), well.value) : undefined;
  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-[2px]" {...(said.rest ? tip(said.rest) : {})}>
      <div className="flex items-center gap-[8px]">
        <span className="min-w-0 flex-1 truncate text-[12px] text-label-2">{said.name}</span>
        <NumberField
          className="w-[72px]"
          label={said.name}
          value={well.value}
          min={well.min}
          max={well.max}
          step={step}
          decimals={decimals}
          suffix={suffix}
          onChange={well.onChange}
        />
      </div>
      <Slider label={said.name} value={well.value} min={well.min} max={well.max} step={step} onChange={well.onChange} />
    </div>
  );
}

/**
 * What the tool will draw WITH — never what is already on the page. The title says so:
 * repainting something already drawn is the selection's business.
 */
function DrawWithBlock({ paint }: { paint: DrawWith }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-[8px]">
      <div className="flex items-center gap-[8px]">
        <span className="w-[44px] shrink-0 text-[12px] text-label-2">Fill</span>
        <ColourField hex={paint.fillOn ? paint.fill : null} ink={paint.fillInk} label="Fill colour to draw with" title="Fill" onChange={paint.onFill} onNone={paint.onFillNone} />
        <span className="tnum min-w-0 flex-1 truncate text-[12px] uppercase text-label-2">{paint.fillOn ? paint.fill : 'None'}</span>
      </div>
      <div className="flex items-center gap-[8px]">
        <span className="w-[44px] shrink-0 text-[12px] text-label-2">Stroke</span>
        <ColourField hex={paint.weight > 0 ? paint.stroke : null} ink={paint.strokeInk} label="Stroke colour to draw with" title="Stroke" onChange={paint.onStroke} onNone={paint.onStrokeNone} />
        <NumberField
          className="min-w-0 flex-1"
          prefix={<Icon name="weight" size={12} />}
          label="Stroke weight to draw with"
          value={paint.weight}
          min={0}
          max={200}
          step={0.5}
          decimals={1}
          onChange={paint.onWeight}
        />
      </div>
    </div>
  );
}

/** "Name — what it does", taken apart. */
export function split(title: string): { name: string; rest: string } {
  const at = title.indexOf(' — ');
  return at < 0 ? { name: title, rest: '' } : { name: title.slice(0, at), rest: title.slice(at + 3) };
}

/** The unit a rail's own formatter adds to its number, so the field can show it once. */
function suffixOf(formatted: string, value: number): string | undefined {
  const bare = formatted.replace(String(Math.round(value)), '').replace(String(value), '').trim();
  return bare && bare.length <= 3 ? bare : undefined;
}
